import React from 'react';
import { useHistory } from 'react-router-dom';
import { StyledButton } from 'src/client/components/ui/Button';
import { GalleryThumbnailGrid, GalleryThumbnailGridItem } from 'src/client/components/grids/GalleryThumbnailGrid';
import { ImageLink } from 'src/client/components/ui/ImageLink';
import { Pagination } from 'src/client/components/ui/Pagination';
import { PaginationModal } from 'src/client/components/modals/PaginationModal';
import { useSfwImagePage } from 'src/client/requests/useSfwImages';
import { useSettingsSelector } from 'src/client/redux/modules/helpers/useSelector';
import { getSfwImagePath, getSfwPagePath } from './paths';

type Props = { page: number; totalPages: number };

export const SfwImagesPage = (props: Props) => {
  const { page, totalPages } = props;
  const history = useHistory();
  const fullThumbnails = useSettingsSelector('thumbnail') as number;
  const { data, isError, refetch } = useSfwImagePage(page);

  const setPage = (nextPage: number | string) => {
    const pageNumber = Number(nextPage);
    if (!Number.isSafeInteger(pageNumber) || pageNumber < 1 || pageNumber > totalPages) return;
    history.push(getSfwPagePath(pageNumber));
  };

  if (isError) {
    return (
      <section>
        <p>Unable to load SFW images.</p>
        <StyledButton onClick={() => refetch()}>Try again</StyledButton>
      </section>
    );
  }

  if (!data) return <section role="status">Loading images…</section>;

  return (
    <section>
      <GalleryThumbnailGrid>
        {data.images.map(({ number, url }) => (
          <GalleryThumbnailGridItem key={number}>
            <ImageLink
              item={{ altText: `SFW image ${number}`, imgSrc: url, url: getSfwImagePath(number) }}
              ariaLabel={`View SFW image ${number} full size`}
              thumbnailMode={+fullThumbnails === 1 ? 'full' : 'square'}
              loading="lazy"
              decoding="async"
            />
          </GalleryThumbnailGridItem>
        ))}
      </GalleryThumbnailGrid>
      <Pagination
        activePage={page}
        totalPages={totalPages}
        pageUrlPrefix="/sfw-images"
        pageUrlFn={(pageNumber) => getSfwPagePath(Number(pageNumber))}
        onPageChange={setPage}
        showPrevNext
        modal={<PaginationModal setPage={setPage} totalPages={totalPages} />}
      />
    </section>
  );
};
